import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, Check, X, Copy, MailCheck } from "lucide-react";
import { WorkspaceShell } from "@/shell/WorkspaceShell";
import { Card, CardBody, Button, Pill } from "@/design/components";
import { useProjects, useAllSubProjects } from "@/domain/hooks";
import { useCreateInvite } from "@/domain/useOnboarding";
import { demoModeActive } from "@/domain/api";

/**
 * Lightweight contractor onboarding for Stratus: choose the tier (prime or sub),
 * the part of the programme the contractor works on — a whole Project for a prime,
 * a specific Sub-Project for a subcontractor (spec §1, §9.3) — then send a secure
 * invite. Renders inside the workspace shell so the sidebar stays available.
 */
export function InviteContractorPage() {
  const projects = useProjects();
  const allSubProjects = useAllSubProjects();
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  const [org, setOrg] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [tier, setTier] = useState<"prime" | "sub">("sub");
  const [projectId, setProjectId] = useState(projects[0]?.id ?? "");
  const [subProjectId, setSubProjectId] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [planAttached, setPlanAttached] = useState(false);
  const { createInvite, isPending } = useCreateInvite();
  const [result, setResult] = useState<{ token: string | null; email: string; emailSent: boolean; returningOrg: boolean; queued: boolean } | null>(null);

  // The projects list starts from the seed (slug ids) on first render, then
  // swaps to API rows (numeric ids) once the programme loads. Re-sync the
  // selected project to a real id whenever the current one isn't in the list,
  // so the sub-project options (keyed on the real parent id) actually populate
  // without the user having to re-pick the project.
  useEffect(() => {
    if (projects.length === 0) return;
    if (!projects.some((p) => p.id === projectId)) {
      setProjectId(projects[0].id);
      setSubProjectId("");
    }
  }, [projects, projectId]);

  const subs = allSubProjects.filter((sp) => sp.projectId === projectId);
  // A subcontractor is scoped to a Sub-Project (spec §1, §9.3), so a sub-project
  // is compulsory for the "sub" tier; a prime is scoped to the whole project.
  // Organisation name + budget fields are hidden (see JSX below).
  const needsSubProject = tier === "sub";
  // The invited person's name — split back into firstname/lastname by the backend.
  const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
  const canSend =
    !!firstName.trim() && !!lastName.trim() && !!email.trim() && !!projectId &&
    (!needsSubProject || !!subProjectId) && !isPending;

  async function send() {
    // A prime lead runs the Tier-2 wizard; a sub lead the Tier-3 wizard. Members
    // and reporters are invited later from within a contractor's own team screen.
    const role = tier === "prime" ? "prime_lead" : "sub_lead";
    // Scope the invite. A subcontractor is attached to a Sub-Project: sub-projects
    // are project rows one level deep, so we send the sub-project's own id and the
    // backend scopes the whole invite (role pack, membership, approval gate) to it.
    // In demo mode the seed sub-projects have no backend row, so fall back to the
    // parent project id — the invite still resolves; scope degrades to the parent
    // (same behaviour as the §9.5 invite modal).
    const targetProjectId =
      tier === "sub" ? (demoModeActive() ? projectId : subProjectId) : projectId;
    const res = await createInvite({
      role,
      inviteeEmail: email.trim(),
      // Org-name field is hidden; the backend still requires a name, so fall back
      // to the contact email.
      inviteeName: fullName || email.trim(),
      projectId: targetProjectId,
      message: message.trim() || undefined,
    });
    if (!res) {
      toast.error("Couldn't create the invite — please retry.");
      return;
    }
    // Three token-less-or-not outcomes:
    //  • queued — the inviter's OWN org isn't approved yet, so the invitee is
    //    held and emailed automatically once that org is approved (spec §9). No
    //    link exists yet — this is NOT "already onboarded".
    //  • returning — the email already belongs to an active org: nothing to
    //    onboard, so no link.
    //  • otherwise — a real onboarding link/token to show and (usually) email.
    const queued = res.queued ?? false;
    const returning = !queued && (res.returningOrg || !res.token);
    setResult({ token: res.token ?? null, email: res.inviteeEmail, emailSent: res.emailSent, returningOrg: returning, queued });
    if (queued) {
      toast.message(`Invite queued for ${res.inviteeEmail} — it'll be emailed once your organisation is approved.`);
    } else if (returning) {
      toast.message(`${fullName || "That contact"} is already onboarded and active — no new invite is needed.`);
    } else {
      toast.success(
        res.emailSent
          ? `Secure invite emailed to ${res.inviteeEmail}`
          : `Invite created for ${res.inviteeEmail} — share the link below`,
      );
    }
  }

  const inviteUrl = result?.token ? `${window.location.origin}/onboard/${result.token}` : "";

  return (
    <WorkspaceShell>
      <div className="mx-auto max-w-3xl px-4 py-5 md:px-8 md:py-6">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Onboard a contractor</h1>
        <p className="mt-1 text-sm text-mute">
          Set which programme this contractor will work on, then send a secure invite. They complete their own banking and team setup from there.
        </p>

        {result ? (
          <InviteSuccess
            orgName={fullName}
            email={result.email}
            emailSent={result.emailSent}
            returningOrg={result.returningOrg}
            queued={result.queued}
            inviteUrl={inviteUrl}
            onDone={() => navigate(`/workspace/${workspaceId}`)}
          />
        ) : (
        <div className="mt-6 space-y-5">
          <Card>
            <CardBody className="space-y-4">
              {/* Organisation name — hidden per request. `org` state is kept so the
                  success screen and toasts still resolve (they fall back to a generic
                  label when empty). Restore this field to collect a display name again.
              <Field label="Organisation name">
                <input value={org} onChange={(e) => setOrg(e.target.value)} placeholder="e.g. Lakeside Mechanical Ltd" className={inp} />
              </Field>
              */}
              <Field label="Tier">
                <div className="flex gap-2">
                  {(["prime", "sub"] as const).map((t) => (
                    <button key={t} onClick={() => { setTier(t); setSubProjectId(""); }} className={`rounded-md border px-3 py-1.5 text-sm ${tier === t ? "border-brand bg-brandSoft text-brand" : "border-line text-mute hover:bg-subtle"}`}>
                      {t === "prime" ? "Prime contractor" : "Subcontractor"}
                    </button>
                  ))}
                </div>
              </Field>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="space-y-4">
              <p className="text-sm font-medium text-ink">Scope</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Project">
                  <select value={projectId} onChange={(e) => { setProjectId(e.target.value); setSubProjectId(""); }} className={inp}>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </Field>
                {/* Prime = whole project (no sub-project). Sub = compulsory
                    sub-project scope (spec §1, §9.3). */}
                {tier === "sub" && (
                  <Field label={<>Sub-project </>}>
                    <select value={subProjectId} onChange={(e) => setSubProjectId(e.target.value)} className={inp} aria-required="true">
                      <option value="" disabled>Select a sub-project…</option>
                      {subs.map((sp) => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
                    </select>
                  </Field>
                )}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="space-y-4">
              <p className="text-sm font-medium text-ink">Invite</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="First name">
                  <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="e.g. Grace" className={inp} />
                </Field>
                <Field label="Last name">
                  <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="e.g. Nansubuga" className={inp} />
                </Field>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Contact email">
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="director@contractor.co.ug" className={inp} />
                </Field>
                <Field label="Personal message (optional)">
                  <input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Short note" className={inp} />
                </Field>
              </div>
              {/* Procurement plan (optional) — hidden per request. The attach control was a
                  non-functional placeholder (no file upload was ever wired to it). Restore
                  this block when the real procurement-upload flow is connected here.
              <div>
                <p className="mb-1 block text-xs font-medium text-faint">Procurement plan (optional)</p>
                {planAttached ? (
                  <div className="flex items-center gap-2 rounded-md border border-ok/40 bg-okSoft px-3 py-2 text-sm text-ok">
                    <Check className="h-4 w-4" /> contractor-scope.pdf attached
                    <button className="ml-auto text-faint hover:text-ink" onClick={() => setPlanAttached(false)} aria-label="Remove"><X className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <button onClick={() => setPlanAttached(true)} className="flex w-full items-center justify-center gap-2 rounded-md border-2 border-dashed border-line py-4 text-sm text-faint hover:border-brand">
                    <UploadCloud className="h-4 w-4" /> Attach a plan if you have one
                  </button>
                )}
                <p className="mt-1 text-xs text-faint">Not required. Add one only if this contractor has a detailed scope document.</p>
              </div>
              */}
            </CardBody>
          </Card>

          <div className="flex items-center justify-between">
            <p className="text-xs text-faint">Secure invite links are single-use and expire after 30 days.</p>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => navigate(`/workspace/${workspaceId}`)}>Cancel</Button>
              <Button disabled={!canSend} onClick={send}>{isPending ? "Creating…" : "Send invite"}</Button>
            </div>
          </div>
        </div>
        )}
      </div>
    </WorkspaceShell>
  );
}

const inp = "w-full rounded-md border border-line bg-card px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand";

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-faint">{label}</span>
      {children}
    </label>
  );
}

/** Confirmation shown after a secure invite is created — mirrors the invite modal:
 *  a copy-able onboarding link (the primary artefact when email delivery is off or
 *  the recipient wants it shared directly). */
function InviteSuccess({
  orgName,
  email,
  emailSent,
  returningOrg,
  queued,
  inviteUrl,
  onDone,
}: {
  orgName: string;
  email: string;
  emailSent: boolean;
  returningOrg: boolean;
  queued: boolean;
  inviteUrl: string;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard?.writeText(inviteUrl).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => toast.error("Couldn't copy — select and copy the link manually."),
    );
  }

  // Queued: the inviter's own org isn't approved yet, so the invite is held and
  // emailed automatically on approval — no link exists yet (spec §9). This is a
  // normal, positive outcome, not "already onboarded".
  if (queued) {
    return (
      <Card className="mt-6">
        <CardBody className="space-y-4">
          <div className="flex items-center gap-2">
            <MailCheck className="h-5 w-5 text-brand" />
            <h2 className="text-base font-semibold text-ink">Invite queued</h2>
          </div>
          <p className="text-sm text-mute">
            The invite for <span className="font-medium text-ink">{email}</span> is queued.
            It'll be emailed automatically once your organisation is approved — nothing is
            sent to them before then.
          </p>
          <div className="flex justify-end">
            <Button onClick={onDone}>Done</Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  // An already-onboarded, active org: nothing to onboard, so no link — just tell
  // the operator why (avoids the earlier broken "/onboard/null" link).
  if (returningOrg) {
    return (
      <Card className="mt-6">
        <CardBody className="space-y-4">
          <div className="flex items-center gap-2">
            <Check className="h-5 w-5 text-ok" />
            <h2 className="text-base font-semibold text-ink">Already onboarded</h2>
          </div>
          <p className="text-sm text-mute">
            <span className="font-medium text-ink">{email}</span> already belongs to an
            organisation that has completed onboarding and is active, so no new invite is
            needed.
          </p>
          <div className="flex justify-end">
            <Button onClick={onDone}>Done</Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mt-6">
      <CardBody className="space-y-4">
        <div className="flex items-center gap-2">
          <MailCheck className="h-5 w-5 text-ok" />
          <h2 className="text-base font-semibold text-ink">
            {emailSent ? "Invite sent" : "Invite created"}
          </h2>
        </div>
        <p className="text-sm text-mute">
          {emailSent ? (
            <>A secure onboarding link was emailed to <span className="font-medium text-ink">{email}</span>. {orgName || "The contractor"} will appear in the onboarding pipeline once they start.</>
          ) : (
            <>The invite email couldn't be sent, so share this secure onboarding link with <span className="font-medium text-ink">{email}</span> directly.</>
          )}
        </p>
        <div>
          <p className="mb-1 block text-xs font-medium text-faint">Secure onboarding link</p>
          <div className="flex items-center gap-2">
            {/* min-w-0: a w-full input's default min-width:auto still wins
                over w-full inside a flex row, so a long monospace link can
                force the row (and the Copy button next to it) wider than
                the viewport instead of the input shrinking to fit. */}
            <input readOnly value={inviteUrl} className={`${inp} min-w-0 font-mono text-xs`} onFocus={(e) => e.currentTarget.select()} />
            <Button variant="ghost" className="shrink-0" onClick={copy}>
              {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy</>}
            </Button>
          </div>
          <p className="mt-1 text-xs text-faint">Single-use · expires in 30 days.</p>
        </div>
        <div className="flex justify-end">
          <Button onClick={onDone}>Done</Button>
        </div>
      </CardBody>
    </Card>
  );
}
